import {useContext, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Add, Check} from '@mui/icons-material';
import {Box, IconButton, Stack, Typography} from '@mui/material';

import {UploadDocumentsModal} from './UploadDocumentsModal';
import {ConfirmDeleteDialog} from '../../../../../components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import {DocumentList} from '../../../../../components/DocumentList/DocumentList';
import {SnackBarContext} from '../../../../../contexts/SnackBarContext/SnackBarContext';
import {useApiWrite} from '../../../../../hooks/useApiWrite';
import {greenColors} from '../../../../../layouts/theme/designSystem';
import {Delete} from '../../../../../lib/api/DELETE/Delete';
import {DeleteRoutes} from '../../../../../lib/api/DELETE/DeleteRoutes';
import {ApplicantDocuments as ApplicantDocumentsType} from '../../types';

interface Props {
  refetchProfile: () => void;
  documents: ApplicantDocumentsType[];
}

export const ApplicantDocuments = ({refetchProfile, documents}: Props) => {
  const {t} = useTranslation();
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const {dispatchMessage} = useContext(SnackBarContext);

  const isCvUploaded = documents.some((doc) => doc.type === 'cv');

  const onSuccess = () => {
    dispatchMessage(t('shared.formSubmit.success'), 'success');
  };

  const {makeRequest} = useApiWrite<Delete.DeleteApplicantDocument>({
    method: 'delete',
    path: DeleteRoutes[Delete.DeleteApplicantDocument](documentToDelete || ''),
    onSuccess: () => {
      refetchProfile();
      onSuccess();
    },
    onError: () => {
      dispatchMessage(t('applicantProfile.documents.dragAndDrop.errors.deleteFailed'), 'error');
    },
  });

  const openDeleteDialog = (resourceId: string) => {
    setDocumentToDelete(resourceId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (documentToDelete) {
      makeRequest(undefined);
      setDocumentToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  return (
    <>
      <Stack spacing={1} mt={5}>
        <Box display={'flex'} justifyContent='space-between' alignItems='center'>
          <Typography variant='h2'>{t('applicantProfile.documents.title')}</Typography>
          <IconButton onClick={() => setOpen(true)} name='add' sx={{color: greenColors[100]}}>
            <Add width={24} height={24} />
          </IconButton>
        </Box>
        <Typography variant='body1' marginRight={2}>
          {t('applicantProfile.documents.description')}
        </Typography>
        {isCvUploaded && (
          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant='body2' color='primary.var70'>
              {t('applicantProfile.documents.uploaded')}
            </Typography>
            <Check sx={{color: greenColors[100], fontSize: 'medium'}} />
          </Stack>
        )}
        <DocumentList documents={documents} onDelete={openDeleteDialog} />
      </Stack>
      <UploadDocumentsModal
        open={open}
        onClose={() => setOpen(false)}
        refetchProfile={refetchProfile}
        isCvUploaded={isCvUploaded}
        onSuccess={onSuccess}
      />
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        handleConfirm={confirmDelete}
        handleCancel={() => setDeleteDialogOpen(false)}
        dialogContent={t('applicantProfile.documents.deleteConfirmation')}
      />
    </>
  );
};
